import { ChevronDown, Download } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { ExportFormat } from "../../api/types";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu";
import { Button } from "../../components/ui";
import { k, t } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";

/** Formats d'export des documents A4 (OPO et mise en ambiance) : libellé et aide, une seule liste pour les deux
 *  menus « Exporter ». La mise en ambiance remplace l'aide des formats dont le contenu diffère (`hints`). */
export const EXPORT_FORMATS: Record<ExportFormat, { label: string; hint: string }> = {
  pdf: { label: k("PDF"), hint: k("Mise en page de l'OPO, prête à diffuser") },
  docx: { label: k("Word (.docx)"), hint: k("Document modifiable dans Word") },
  odt: { label: k("OpenDocument (.odt)"), hint: k("LibreOffice, OpenOffice") },
  txt: { label: k("Texte (.txt)"), hint: k("Texte brut, pour une messagerie") },
  json: { label: k("Fichier d'échange Generex"), hint: k("Ordre complet (textes, éditions, signature), réimportable") },
};

/** Menu « Exporter » d'un document A4 (R9) : bouton principal de l'en-tête de la vue, menu partagé (Échap, clic
 *  dehors ou choix le ferment, jamais la sortie de la souris), une entrée par format avec son aide en seconde
 *  ligne. `children` : entrées complémentaires après un séparateur (import d'un fichier d'échange). */
export function ExportMenu({
  formats,
  href,
  hints,
  disabled,
  blocked,
  onBlocked,
  title,
  variant = "primary",
  children,
}: {
  formats: readonly ExportFormat[];
  href: (format: ExportFormat) => string;
  /** Aide propre à ce document (clé française, traduite ici). */
  hints?: Partial<Record<ExportFormat, string>>;
  /** Requête en cours (rédaction) ou document absent. */
  disabled?: boolean;
  /** Export impossible pour une raison explicable (ordre composé sur le terrain précédent d'une zone libre) : les
   *  entrées de format restent visibles, estompées, et désignent l'action attendue (`onBlocked`, R12) ; les
   *  entrées complémentaires (import d'un fichier d'échange) restent utilisables. Sans entrée complémentaire, le
   *  bouton lui-même est bloqué (rien à déplier). */
  blocked?: boolean;
  onBlocked?: () => void;
  title?: string;
  /** Bouton secondaire quand une autre action est l'action attendue de l'en-tête (un seul bouton principal, R5). */
  variant?: "primary" | "secondary";
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Licence d'essai (registre F44) : aucun format exportable, quelle que soit l'autre raison ; le survol et l'appui
  // le disent. Les entrées complémentaires (import d'un fichier d'échange) restent utilisables.
  const trial = useTrialExport();
  const isBlocked = trial.blocked || blocked;
  const explain = trial.blocked ? trial.onBlocked : onBlocked;
  const blockTrigger = isBlocked && !children;
  const waitInstead = () => {
    setOpen(false);
    explain?.();
  };
  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      align="end"
      width={330}
      ariaLabel={title ?? t("Exporter")}
      trigger={
        <Button
          size="sm"
          variant={variant}
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          blocked={blockTrigger}
          onBlocked={explain}
          title={trial.title ?? title}
        >
          <Download size={14} /> {t("Exporter")} <ChevronDown size={14} />
        </Button>
      }
    >
      {formats.map((format) => (
        <MenuItem
          key={format}
          href={href(format)}
          label={t(EXPORT_FORMATS[format].label)}
          hint={t(hints?.[format] ?? EXPORT_FORMATS[format].hint)}
          blocked={isBlocked}
          onBlocked={waitInstead}
          title={trial.title}
        />
      ))}
      {children && (
        <>
          <MenuSeparator />
          {children}
        </>
      )}
    </Menu>
  );
}
